// src/services/export/MapExportService.ts
// Servicio de exportación de mapas PTEL a PNG/PDF
// ADV-03: Export mapa PNG/PDF

import html2canvas from 'html2canvas';

/**
 * Configuración de exportación de mapa
 */
export interface MapExportConfig {
  /** Referencia al contenedor del mapa */
  mapContainer: HTMLElement;
  /** Nombre base del archivo */
  filename: string;
  /** Metadatos del municipio */
  metadata: MapExportMetadata;
  /** Opciones de imagen */
  imageOptions?: MapImageOptions;
}

/**
 * Metadatos del municipio para cabecera PDF
 */
export interface MapExportMetadata {
  /** Código INE del municipio */
  codigoINE: string;
  /** Nombre del municipio */
  nombreMunicipio: string;
  /** Provincia */
  provincia: string;
  /** Fecha de generación (ISO string o Date) */
  fechaGeneracion?: string | Date;
  /** Total de infraestructuras mostradas */
  totalInfraestructuras?: number;
  /** Nombre del archivo PTEL origen */
  archivoOrigen?: string;
}

/**
 * Opciones de configuración de imagen
 */
export interface MapImageOptions {
  /** Factor de escala (default: 2 para alta resolución) */
  scale?: number;
  /** Ancho mínimo en píxeles */
  minWidth?: number;
  /** Alto mínimo en píxeles */
  minHeight?: number;
  /** Color de fondo */
  backgroundColor?: string;
  /** Incluir leyenda en exportación */
  includeLegend?: boolean;
}

/**
 * Resultado de exportación de mapa
 */
export interface MapExportResult {
  /** Éxito de la operación */
  success: boolean;
  /** Nombre del archivo generado */
  filename: string;
  /** Formato de salida */
  format: 'png' | 'pdf';
  /** Tamaño en bytes */
  size: number;
  /** Blob del archivo */
  blob?: Blob;
  /** Data URL (para preview) */
  dataUrl?: string;
  /** Mensaje de error si falla */
  error?: string;
  /** Dimensiones de la imagen */
  dimensions?: { width: number; height: number };
}

/**
 * Opciones específicas para PDF
 */
export interface PdfExportOptions {
  /** Orientación de página */
  orientation?: 'portrait' | 'landscape';
  /** Tamaño de página */
  pageSize?: 'a4' | 'a3' | 'letter';
  /** Incluir cabecera con metadatos */
  includeHeader?: boolean;
  /** Incluir pie de página */
  includeFooter?: boolean;
  /** Margen en mm */
  margin?: number;
}

// Valores por defecto
const DEFAULT_IMAGE_OPTIONS: Required<MapImageOptions> = {
  scale: 2,
  minWidth: 1920,
  minHeight: 1080,
  backgroundColor: '#ffffff',
  includeLegend: true,
};

const DEFAULT_PDF_OPTIONS: Required<PdfExportOptions> = {
  orientation: 'landscape',
  pageSize: 'a4',
  includeHeader: true,
  includeFooter: true,
  margin: 10,
};

/**
 * Servicio de exportación de mapas PTEL
 * Genera archivos PNG y PDF del visor cartográfico
 */
export class MapExportService {
  /**
   * Exporta el mapa actual como imagen PNG
   */
  static async exportToPNG(config: MapExportConfig): Promise<MapExportResult> {
    const { mapContainer, filename, imageOptions = {} } = config;
    const opts = { ...DEFAULT_IMAGE_OPTIONS, ...imageOptions };

    try {
      // Captura el contenedor del mapa
      const canvas = await html2canvas(mapContainer, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: opts.backgroundColor,
        scale: opts.scale,
        logging: false,
        // Asegurar dimensiones mínimas
        width: Math.max(mapContainer.offsetWidth, opts.minWidth / opts.scale),
        height: Math.max(mapContainer.offsetHeight, opts.minHeight / opts.scale),
      });

      // Convertir a blob PNG
      const blob = await this.canvasToBlob(canvas, 'image/png');
      const dataUrl = canvas.toDataURL('image/png');

      // Generar nombre de archivo con timestamp
      const finalFilename = this.generateFilename(filename, 'png');

      return {
        success: true,
        filename: finalFilename,
        format: 'png',
        size: blob.size,
        blob,
        dataUrl,
        dimensions: {
          width: canvas.width,
          height: canvas.height,
        },
      };
    } catch (error) {
      return {
        success: false,
        filename: '',
        format: 'png',
        size: 0,
        error: error instanceof Error ? error.message : 'Error capturando mapa',
      };
    }
  }

  /**
   * Exporta el mapa actual como documento PDF
   * Requiere jsPDF instalado: npm install jspdf
   */
  static async exportToPDF(
    config: MapExportConfig,
    pdfOptions: PdfExportOptions = {}
  ): Promise<MapExportResult> {
    const opts = { ...DEFAULT_PDF_OPTIONS, ...pdfOptions };
    const { mapContainer, filename, metadata, imageOptions = {} } = config;
    const imgOpts = { ...DEFAULT_IMAGE_OPTIONS, ...imageOptions };

    try {
      // Importación dinámica de jsPDF
      const { default: jsPDF } = await import('jspdf');

      // Captura el mapa como imagen
      const canvas = await html2canvas(mapContainer, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: imgOpts.backgroundColor,
        scale: imgOpts.scale,
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');

      // Crear documento PDF
      const pdf = new jsPDF({
        orientation: opts.orientation,
        unit: 'mm',
        format: opts.pageSize,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = opts.margin;

      let yPosition = margin;

      // Cabecera con metadatos del municipio
      if (opts.includeHeader) {
        yPosition = this.addPdfHeader(pdf, metadata, margin, pageWidth);
      }

      // Calcular dimensiones de imagen manteniendo proporción
      const imgWidth = pageWidth - 2 * margin;
      const imgHeight = (canvas.height / canvas.width) * imgWidth;
      const availableHeight = pageHeight - yPosition - margin - (opts.includeFooter ? 15 : 0);

      // Ajustar si la imagen es muy alta
      const finalImgHeight = Math.min(imgHeight, availableHeight);
      const finalImgWidth = (finalImgHeight / imgHeight) * imgWidth;

      // Centrar imagen horizontalmente
      const xOffset = (pageWidth - finalImgWidth) / 2;

      // Añadir imagen del mapa
      pdf.addImage(imgData, 'PNG', xOffset, yPosition, finalImgWidth, finalImgHeight);

      // Pie de página
      if (opts.includeFooter) {
        this.addPdfFooter(pdf, metadata, pageWidth, pageHeight, margin);
      }

      // Generar blob
      const pdfBlob = pdf.output('blob');
      const finalFilename = this.generateFilename(filename, 'pdf');

      return {
        success: true,
        filename: finalFilename,
        format: 'pdf',
        size: pdfBlob.size,
        blob: pdfBlob,
        dimensions: {
          width: canvas.width,
          height: canvas.height,
        },
      };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Error generando PDF';
      
      // Detectar si falta jsPDF
      if (errorMsg.includes('jspdf') || errorMsg.includes('module')) {
        return {
          success: false,
          filename: '',
          format: 'pdf',
          size: 0,
          error: 'jsPDF no instalado. Ejecute: npm install jspdf',
        };
      }

      return {
        success: false,
        filename: '',
        format: 'pdf',
        size: 0,
        error: errorMsg,
      };
    }
  }

  /**
   * Añade cabecera al PDF con metadatos del municipio
   */
  private static addPdfHeader(
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports -- import() inline para no importar jspdf en top-level (bundle condicional)
    pdf: import('jspdf').jsPDF,
    metadata: MapExportMetadata,
    margin: number,
    pageWidth: number
  ): number {
    const { codigoINE, nombreMunicipio, provincia, totalInfraestructuras } = metadata;
    
    // Título principal
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`PTEL - ${nombreMunicipio}`, pageWidth / 2, margin + 5, { align: 'center' });

    // Subtítulo con provincia y código INE
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(
      `${provincia} | Código INE: ${codigoINE}`,
      pageWidth / 2,
      margin + 12,
      { align: 'center' }
    );

    // Info de infraestructuras si está disponible
    if (totalInfraestructuras !== undefined) {
      pdf.setFontSize(9);
      pdf.text(
        `Infraestructuras representadas: ${totalInfraestructuras}`,
        pageWidth / 2,
        margin + 18,
        { align: 'center' }
      );
      return margin + 25;
    }

    return margin + 20;
  }

  /**
   * Añade pie de página al PDF
   */
  private static addPdfFooter(
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports -- import() inline para no importar jspdf en top-level (bundle condicional)
    pdf: import('jspdf').jsPDF,
    metadata: MapExportMetadata,
    pageWidth: number,
    pageHeight: number,
    margin: number
  ): void {
    const fecha = metadata.fechaGeneracion
      ? this.formatDate(metadata.fechaGeneracion)
      : this.formatDate(new Date());

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);

    // Fecha a la izquierda
    pdf.text(`Generado: ${fecha}`, margin, pageHeight - margin);

    // Créditos a la derecha
    pdf.text(
      'PTEL Tool · Herramienta en desarrollo · Sin carácter oficial',
      pageWidth - margin,
      pageHeight - margin,
      { align: 'right' }
    );

    // Archivo origen si está disponible
    if (metadata.archivoOrigen) {
      pdf.text(
        `Origen: ${metadata.archivoOrigen}`,
        pageWidth / 2,
        pageHeight - margin,
        { align: 'center' }
      );
    }

    // Restaurar color
    pdf.setTextColor(0, 0, 0);
  }

  /**
   * Genera nombre de archivo con timestamp
   */
  static generateFilename(baseName: string, extension: 'png' | 'pdf'): string {
    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const sanitized = baseName
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .replace(/_+/g, '_')
      .substring(0, 50);
    return `${sanitized}_PTEL_${timestamp}.${extension}`;
  }

  /**
   * Formatea fecha para visualización
   */
  private static formatDate(date: string | Date): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Convierte canvas a Blob
   */
  private static canvasToBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Error generando blob desde canvas'));
          }
        },
        type,
        1.0 // Máxima calidad
      );
    });
  }

  /**
   * Descarga el resultado de exportación
   */
  static download(result: MapExportResult): boolean {
    if (!result.success || !result.blob) {
      return false;
    }

    const url = URL.createObjectURL(result.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = result.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return true;
  }

  /**
   * Exporta y descarga en un solo paso
   */
  static async exportAndDownload(
    config: MapExportConfig,
    format: 'png' | 'pdf',
    pdfOptions?: PdfExportOptions
  ): Promise<MapExportResult> {
    const result =
      format === 'png'
        ? await this.exportToPNG(config)
        : await this.exportToPDF(config, pdfOptions);

    if (result.success) {
      this.download(result);
    }

    return result;
  }
}

export default MapExportService;
